import React, { useState } from "react";
import { doc, updateDoc, addDoc, collection, getDocs, query, where, writeBatch, deleteDoc } from "firebase/firestore";
import { db } from "../firebase/db"; // Adjust the import based on your project structure
import AssignCardModal from "../components/AssignCardModal"; // Adjust the import based on your project structure
import CardDetailModal from "../components/CardDetailModal"; // Add this import if not present
import { useAuth } from "../context/AuthContext"; // Add this import
import { Card } from "../types";
import Navbar from "./Navbar"; // Add this import at the top
import Toast from "../components/Toast";

type Slot = {
  row: number;
  col: number;
  cardId?: string | null;
  side?: "front" | "back";
  page: number; // <-- Add this line
};
type Page = { front: Slot[]; back: Slot[] };
type Binder = {
  id: string; // <-- Add this line
  name: string;
  pages: Page[];
  columns: number;
  rows: number;
};

type BinderViewProps = {
  binder: any;
  onClose: () => void;
  inventory: Card[];
  wishlist: Card[];
  onSlotClick?: (slot: any) => void; // optional
};

const BinderView: React.FC<BinderViewProps> = ({
  binder,
  onClose,
  inventory,
  wishlist,
  onSlotClick,
}) => {
  const { user } = useAuth();
  const [spreadIdx, setSpreadIdx] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState<{
    page: number;
    side: "front" | "back";
    row: number;
    col: number;
  } | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null); // Add this state

  const totalSpreads = binder.pages.length;

  // Dynamic sizing based on binder size
  let slotSize = "w-16 sm:w-20 md:w-24";
  let imgSize = "w-16 h-24";
  let innerSize = "w-16 h-28";

  if (binder.rows <= 2 && binder.columns <= 2) {
    slotSize = "w-28 sm:w-36 md:w-44";
    imgSize = "w-28 h-40";
    innerSize = "w-28 h-44";
  } else if (binder.rows <= 3 && binder.columns <= 3) {
    slotSize = "w-20 sm:w-28 md:w-32";
    imgSize = "w-20 h-32";
    innerSize = "w-20 h-36";
  }
  // else use default for 4x4 and larger

  // Helper to render a side as a grid
  const renderGrid = (slots: Slot[], side: "front" | "back") => (
    <div className="w-full flex justify-center">
      <div className="flex flex-col gap-2">
        {Array.from({ length: binder.rows }).map((_, row) => (
          <div key={row} className="flex gap-2 justify-center">
            {Array.from({ length: binder.columns }).map((_, col) => {
              const slot = slots.find(s => s.row === row && s.col === col);
              const slotKey = `row-${row}-col-${col}`;
              let card = slot?.cardId
                ? inventory.find((c: Card) => c.id === slot.cardId)
                : null;
              let isWishlist = false;
              if (!card && slot?.cardId) {
                card = wishlist.find((c: Card) => c.id === slot.cardId);
                isWishlist = !!card;
              }

              // Determine clickability and handler
              let clickable = false;
              let handleClick = undefined;
              if (onSlotClick) {
                clickable = true;
                handleClick = () => {
                  setSelectedSlot({
                    page: side === "back" ? spreadIdx - 1 : spreadIdx,
                    side,
                    row,
                    col
                  });
                  onSlotClick(slot);
                };
              } else if (card) {
                clickable = true;
                handleClick = () => setSelectedCard(card!);
              }

              return (
                <div
                  key={slotKey}
                  className={`binder-slot relative border rounded-xl flex flex-col items-center justify-center aspect-square ${slotSize}
                    transition-transform duration-200
                    ${clickable ? "cursor-pointer hover:scale-110 hover:bg-indigo-50 hover:shadow-lg" : "cursor-default"}
                    ${isWishlist ? "border-dashed border-2 border-yellow-400 bg-yellow-50 opacity-80" : "border-gray-200"}
                    ${card ? "shadow bg-white" : "bg-white"}
                  `}
                  onClick={handleClick}
                >
                  {card && onSlotClick && (
                    <button
                      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-indigo-500 text-white shadow hover:bg-indigo-700 transition z-10"
                      style={{ padding: 0 }}
                      onClick={e => {
                        e.stopPropagation();
                        if (slot) {
                          handleRemoveFromBinder({ ...slot, side, page: side === "back" ? spreadIdx - 1 : spreadIdx });
                        }
                      }}
                      title="Remove from Binder"
                      aria-label="Remove from Binder"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  <div className={`flex flex-col items-center justify-center ${innerSize}`}>
                    {card ? (
                      <img src={card.image} alt={card.name} className={`${imgSize} object-contain rounded`} />
                    ) : (
                      <span className="text-gray-300 text-xs italic">Empty</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  // Determine what to show for this spread
  let leftSide: React.ReactNode = null;
  let rightSide: React.ReactNode = null;
  let leftLabel = "";
  let rightLabel = "";

  if (spreadIdx === 0) {
    // First spread: only right side (front of page 1)
    rightSide = renderGrid(binder.pages[0].front, "front");
    rightLabel = `Page 1 (Front)`;
  } else if (spreadIdx === totalSpreads) {
    // Last spread: only left side (back of last page)
    leftSide = renderGrid(binder.pages[binder.pages.length - 1].back, "back");
    leftLabel = `Page ${binder.pages.length} (Back)`;
  } else {
    // Middle spreads: left = back of N, right = front of N+1
    leftSide = renderGrid(binder.pages[spreadIdx - 1].back, "back");
    leftLabel = `Page ${spreadIdx} (Back)`;
    rightSide = renderGrid(binder.pages[spreadIdx].front, "front");
    rightLabel = `Page ${spreadIdx + 1} (Front)`;
  }

  // --- handleAssignFromInventory ---
  const handleAssignFromInventory = async (cardId: string) => {
    if (!user || !selectedSlot) return;

    // Check if card is in inventory or wishlist
    const isInventoryCard = inventory.some(card => card.id === cardId);
    const isWishlistCard = wishlist.some(card => card.id === cardId);

    if (isInventoryCard) {
      await updateDoc(doc(db, "users", user.uid, "inventory", cardId), {
        assignedBinderId: binder.id,
        binderName: binder.name,
        assignedSlot: selectedSlot,
      });
    } else if (isWishlistCard) {
      await updateDoc(doc(db, "users", user.uid, "wishlist", cardId), {
        assignedBinderId: binder.id,
        binderName: binder.name,
        assignedSlot: selectedSlot,
      });
    }

    const binderRef = doc(db, "users", user.uid, "binders", binder.id);
    const updatedPages = [...binder.pages];

    // Remove cardId from any slot that currently has it
    for (const page of updatedPages) {
      for (const side of ["front", "back"] as const) {
        for (const slot of page[side]) {
          if (slot.cardId === cardId) {
            slot.cardId = null;
          }
        }
      }
    }

    // Set cardId on the new slot, and clear previous card's assignment if swapping
    // @ts-ignore
    const slots = updatedPages[selectedSlot.page][selectedSlot.side];
    const slotIndex = slots.findIndex(
      (s: any) => s.row === selectedSlot.row && s.col === selectedSlot.col
    );
    if (slotIndex !== -1) {
      const prevCardId = slots[slotIndex].cardId;
      if (prevCardId && prevCardId !== cardId) {
        const inventoryCard = inventory.find(c => c.id === prevCardId);
        if (inventoryCard) {
          await updateDoc(doc(db, "users", user.uid, "inventory", prevCardId), {
            assignedBinderId: null,
            binderName: null,
            assignedSlot: null,
          });
        } else {
          const wishlistCard = wishlist.find(c => c.id === prevCardId);
          if (wishlistCard) {
            await updateDoc(doc(db, "users", user.uid, "wishlist", prevCardId), {
              assignedBinderId: null,
              binderName: null,
              assignedSlot: null,
            });
          }
        }
      }
      slots[slotIndex] = { ...slots[slotIndex], cardId };
    }
    await updateDoc(binderRef, { pages: updatedPages });

    setSuccessMsg("Card assigned successfully!");
    setSelectedSlot(null);
  };

  // --- handleAssignFromSearch ---
  const handleAssignFromSearch = async (cardData: any) => {
    if (!user || !selectedSlot) return;

    // 1. Add to inventory
    const cardRef = await addDoc(collection(db, "users", user.uid, "inventory"), {
      ...cardData,
      assignedBinderId: binder.id,
      binderName: binder.name,
      assignedSlot: selectedSlot,
      createdAt: new Date(),
    });

    // 2. Update binder slot in Firestore
    const binderRef = doc(db, "users", user.uid, "binders", binder.id);
    const updatedPages = [...binder.pages];

    // Remove cardId from any slot that currently has it
    for (const page of updatedPages) {
      for (const side of ["front", "back"] as const) {
        for (const slot of page[side]) {
          if (slot.cardId === cardRef.id) {
            slot.cardId = null;
          }
        }
      }
    }

    // Set cardId on the new slot, and clear previous card's assignment if swapping
    // @ts-ignore
    const slots = updatedPages[selectedSlot.page][selectedSlot.side];
    const slotIndex = slots.findIndex(
      (s: any) => s.row === selectedSlot.row && s.col === selectedSlot.col
    );
    if (slotIndex !== -1) {
      const prevCardId = slots[slotIndex].cardId;
      if (prevCardId && prevCardId !== cardRef.id) {
        const inventoryCard = inventory.find(c => c.id === prevCardId);
        if (inventoryCard) {
          await updateDoc(doc(db, "users", user.uid, "inventory", prevCardId), {
            assignedBinderId: null,
            binderName: null,
            assignedSlot: null,
          });
        } else {
          const wishlistCard = wishlist.find(c => c.id === prevCardId);
          if (wishlistCard) {
            await updateDoc(doc(db, "users", user.uid, "wishlist", prevCardId), {
              assignedBinderId: null,
              binderName: null,
              assignedSlot: null,
            });
          }
        }
      }
      slots[slotIndex] = { ...slots[slotIndex], cardId: cardRef.id };
    }
    await updateDoc(binderRef, { pages: updatedPages });

    setSuccessMsg("Card added and assigned successfully!");
    setSelectedSlot(null);
  };

  // --- handleAssignFromWishlist ---
  const handleAssignFromWishlist = async (cardData: any) => {
    if (!user || !selectedSlot) return;

    // 1. Add to wishlist collection
    const cardRef = await addDoc(collection(db, "users", user.uid, "wishlist"), {
      ...cardData,
      assignedBinderId: binder.id,
      binderName: binder.name,
      assignedSlot: selectedSlot,
      createdAt: new Date(),
    });

    // 2. Update binder slot in Firestore
    const binderRef = doc(db, "users", user.uid, "binders", binder.id);
    const updatedPages = [...binder.pages];

    // Remove cardId from any slot that currently has it
    for (const page of updatedPages) {
      for (const side of ["front", "back"] as const) {
        for (const slot of page[side]) {
          if (slot.cardId === cardRef.id) {
            slot.cardId = null;
          }
        }
      }
    }

    // Set cardId on the new slot, and clear previous card's assignment if swapping
    // @ts-ignore
    const slots = updatedPages[selectedSlot.page][selectedSlot.side];
    const slotIndex = slots.findIndex(
      (s: any) => s.row === selectedSlot.row && s.col === selectedSlot.col
    );
    if (slotIndex !== -1) {
      const prevCardId = slots[slotIndex].cardId;
      if (prevCardId && prevCardId !== cardRef.id) {
        const inventoryCard = inventory.find(c => c.id === prevCardId);
        if (inventoryCard) {
          await updateDoc(doc(db, "users", user.uid, "inventory", prevCardId), {
            assignedBinderId: null,
            binderName: null,
            assignedSlot: null,
          });
        } else {
          const wishlistCard = wishlist.find(c => c.id === prevCardId);
          if (wishlistCard) {
            await updateDoc(doc(db, "users", user.uid, "wishlist", prevCardId), {
              assignedBinderId: null,
              binderName: null,
              assignedSlot: null,
            });
          }
        }
      }
      slots[slotIndex] = { ...slots[slotIndex], cardId: cardRef.id };
    }
    await updateDoc(binderRef, { pages: updatedPages });

    setSuccessMsg("Wishlist card assigned successfully!");
    setSelectedSlot(null);
  };

  const handleRemoveFromBinder = async (slotToClear: Slot) => {
    if (!user) return;

    const updatedPages = [...binder.pages];
    const pageIdx = slotToClear.page;
    const side = slotToClear.side || "front";

    // Guard: Ensure page index is valid
    if (
      pageIdx < 0 ||
      pageIdx >= updatedPages.length ||
      !updatedPages[pageIdx] ||
      !updatedPages[pageIdx][side]
    ) {
      setErrorMsg("Could not find the correct page/side for removal.");
      return;
    }

    // @ts-ignore
    const slots = updatedPages[pageIdx][side];
    const slotIndex = slots.findIndex(
      (s: any) => s.row === slotToClear.row && s.col === slotToClear.col
    );
    if (slotIndex !== -1) {
      // Save cardId before clearing
      const cardId = slots[slotIndex].cardId;
      slots[slotIndex] = { ...slots[slotIndex], cardId: null };

      // Remove assignment info from the card in inventory or wishlist
      if (cardId) {
        // Try inventory first
        const inventoryCard = inventory.find(c => c.id === cardId);
        if (inventoryCard) {
          await updateDoc(doc(db, "users", user.uid, "inventory", cardId), {
            assignedBinderId: null,
            binderName: null,
            assignedSlot: null,
          });
        } else {
          // Try wishlist
          const wishlistCard = wishlist.find(c => c.id === cardId);
          if (wishlistCard) {
            await updateDoc(doc(db, "users", user.uid, "wishlist", cardId), {
              assignedBinderId: null,
              binderName: null,
              assignedSlot: null,
            });
          }
        }
      }
    }

    const binderRef = doc(db, "users", user.uid, "binders", binder.id);
    await updateDoc(binderRef, { pages: updatedPages });

    setSuccessMsg("Card removed from binder!");
  };

  // Call this before deleting the binder
  const clearBinderAssignments = async (binderId: string, userId: string) => {
    // Inventory
    const inventoryRef = collection(db, "users", userId, "inventory");
    const inventoryQuery = query(inventoryRef, where("assignedBinderId", "==", binderId));
    const inventorySnap = await getDocs(inventoryQuery);

    // Wishlist
    const wishlistRef = collection(db, "users", userId, "wishlist");
    const wishlistQuery = query(wishlistRef, where("assignedBinderId", "==", binderId));
    const wishlistSnap = await getDocs(wishlistQuery);

    const batch = writeBatch(db);

    inventorySnap.forEach(docSnap => {
      batch.update(docSnap.ref, {
        assignedBinderId: null,
        binderName: null,
        assignedSlot: null,
      });
    });

    wishlistSnap.forEach(docSnap => {
      batch.update(docSnap.ref, {
        assignedBinderId: null,
        binderName: null,
        assignedSlot: null,
      });
    });

    await batch.commit();
  };

  const handleDeleteBinder = async () => {
    if (!user) return;

    // Clear assignments first
    await clearBinderAssignments(binder.id, user.uid);
    await deleteDoc(doc(db, "users", user.uid, "binders", binder.id));
  };

  return (
    <div className="fixed inset-0 bg-blue-100 z-50 flex flex-col min-h-[700px] min-w-[350px] sm:min-h-[800px] sm:min-w-[600px]">
      {/* Navbar */}
      <Navbar />
      {/* Close (X) Button */}
      {!selectedSlot && !selectedCard && (
        <button
          onClick={onClose}
          className="absolute top-20 right-8 flex items-center gap-2 px-3 h-8 rounded-full bg-indigo-500 text-white shadow hover:bg-indigo-700 transition z-50 font-semibold text-sm"
          aria-label="Back to Inventory"
          style={{ zIndex: 60 }}
        >
          {/* X icon */}
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span>Back to Inventory</span>
        </button>
      )}
      {/* Binder Title */}
      <div className="flex justify-center items-center mt-14 mb-4">
        <h2 className="font-bold text-lg sm:text-xl text-indigo-700 bg-white rounded-xl shadow px-6 py-3">
          {binder.name} <span className="font-normal text-gray-500">- Binder View</span>
        </h2>
      </div>
      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center overflow-x-auto py-0 -mt-24">
        {leftSide && !rightSide ? (
          <div className="flex flex-col items-center max-w-2xl mx-auto">
            <div className="mb-2 text-base sm:text-lg font-bold text-gray-700">{leftLabel}</div>
            {leftSide}
          </div>
        ) : !leftSide && rightSide ? (
          <div className="flex flex-col items-center max-w-2xl mx-auto">
            <div className="mb-2 text-base sm:text-lg font-bold text-gray-700">{rightLabel}</div>
            {rightSide}
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-6 max-w-5xl justify-center items-center mx-auto">
            <div className="flex-1 flex flex-col items-center min-w-[140px]">
              <div className="mb-2 text-base sm:text-lg font-bold text-gray-700">{leftLabel}</div>
              {leftSide}
            </div>
            <div className="flex-1 flex flex-col items-center min-w-[140px]">
              <div className="mb-2 text-base sm:text-lg font-bold text-gray-700">{rightLabel}</div>
              {rightSide}
            </div>
          </div>
        )}
        {/* Navigation Buttons */}
        <div className="flex justify-center gap-2 mt-4 bg-white shadow rounded-xl px-6 py-3">
          <button
            onClick={() => setSpreadIdx(idx => Math.max(0, idx - 1))}
            disabled={spreadIdx === 0}
            className="px-2 py-1 rounded bg-indigo-500 text-white text-sm font-semibold shadow hover:bg-indigo-600 transition disabled:opacity-50"
          >
            Prev
          </button>
          <button
            onClick={() => setSpreadIdx(idx => Math.min(totalSpreads, idx + 1))}
            disabled={spreadIdx === totalSpreads}
            className="px-2 py-1 rounded bg-indigo-500 text-white text-sm font-semibold shadow hover:bg-indigo-600 transition disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
      {/* Assign Modal */}
      {selectedSlot && (
        <AssignCardModal
          inventory={inventory}
          wishlist={wishlist}
          onAssignFromInventory={handleAssignFromInventory}
          onAssignFromSearch={handleAssignFromSearch}
          onAssignFromWishlist={handleAssignFromWishlist}
          onClose={() => setSelectedSlot(null)}
        />
      )}
      {/* Success Message */}
      {successMsg && (
        <Toast
          message={successMsg}
          type="success"
          onClose={() => setSuccessMsg(null)}
        />
      )}
      {errorMsg && (
        <Toast
          message={errorMsg}
          type="error"
          onClose={() => setErrorMsg(null)}
        />
      )}
      {/* Card Detail Modal for public view */}
      {selectedCard && (
        <CardDetailModal card={selectedCard} onClose={() => setSelectedCard(null)} />
      )}
    </div>
  );
};

export default BinderView;